import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { ArrowRight } from 'lucide-react'

const CTA = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  return (
    <section id="applications" ref={ref} className="section-padding bg-background relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5" />
      <motion.div
        className="absolute top-20 left-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 right-20 w-32 h-32 bg-primary/15 rounded-full blur-2xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.7, 0.4],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="container-custom relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center max-w-4xl mx-auto"
        >
          {/* Main CTA */}
          <motion.div variants={itemVariants}>
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Explore Our <span className="gradient-text">Applications</span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 mb-16 leading-relaxed">
              Discover the custom applications and DevOps solutions we've built. 
              Each project includes detailed insights and links to live applications.
            </p>
          </motion.div>

          {/* Features */}
          <motion.div
            variants={itemVariants}
            className="grid md:grid-cols-3 gap-8 text-center"
          >
            <div className="p-6 bg-background/50 backdrop-blur-sm border border-gray-800 rounded-2xl">
              <div className="text-2xl font-bold gradient-text mb-2">Custom Built</div>
              <div className="text-gray-300">
                Every application is tailored to solve specific business challenges
              </div>
            </div>
            <div className="p-6 bg-background/50 backdrop-blur-sm border border-gray-800 rounded-2xl">
              <div className="text-2xl font-bold gradient-text mb-2">Modern Stack</div>
              <div className="text-gray-300">
                Built with the latest technologies and best practices
              </div>
            </div>
            <div className="p-6 bg-background/50 backdrop-blur-sm border border-gray-800 rounded-2xl">
              <div className="text-2xl font-bold gradient-text mb-2">Scalable Solutions</div>
              <div className="text-gray-300">
                Applications designed to grow with your business needs
              </div>
            </div>
          </motion.div>

          {/* Bottom Note */}
          <motion.p
            variants={itemVariants}
            className="text-gray-400 mt-12 text-sm max-w-2xl mx-auto"
          >
            Each application includes a detailed blog post explaining the problem, solution, and technologies used.
          </motion.p>
        </motion.div>
      </div>
    </section>
  )
}

export default CTA
